import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { API_ENDPOINT } from "../../config";
import { Cart } from "../../interfaces";
import { toastMessage } from "../../utils/toastError";
import { fulfilmentApi } from "../fulfilment/fulfilment.api";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

export type UpdateCartItemPayload = {
  id:              string;
  item_name?:      string;
  price_snapshot?: number;
  quantity?:       number;
  description?:    string | null;
  due_date?:       string | null;
};

export type CartItemRow = {
  id:             string;
  item_name:      string;
  description:    string | null;
  price_snapshot: number;
  quantity:       number;
  due_date:       string | null;
  item_type:      string | null;
  added_by_admin: boolean;
  sku_items:      any[] | null;
  created_at:     string;
  updated_at:     string;
  batch_code:     string | null;
  course_code:    string | null;
  course_name:    string | null;
  batch: {
    id:          string;
    name:        string;
    batch_code:  string | null;
    course_code: string | null;
    start_date:  string | null;
    end_date:    string | null;
  } | null;
  cart: {
    id:           string;
    status:       string;
    updated_at:   string;
    payment_date?: string | null;
  } | null;
  student: {
    id:                  string;
    name:                string | null;
    email:               string | null;
    mobile_number:       string | null;
    phone:               string | null;
    alternate_number?:   string | null;
    registration_number: string | null;
  } | null;
};

export type GetCartItemsParams = Partial<{
  student_id:          string;
  email:               string;
  name:                string;
  mobile_number:       string;
  registration_number: string;
  item_name:           string;
  batch_code:          string;
  course_code:         string;
  global_search:       string;
  added_by_admin:      "true" | "false";
  status:              string;
  from:                string;
  to:                  string;
  skip:                number;
  take:                number;
  sort:                "created_at" | "updated_at" | "price_snapshot" | "item_name";
  orderby:             "ASC" | "DESC";
}>;

export interface CartMarketingAnalyticsResponse {
  meta: {
    from: string;
    to: string;
    period: "daily" | "monthly" | "yearly";
  };
  summary: {
    totalAdded: number;
    totalPaid: number;
    conversionRate: string;
    totalRevenue: number;
  };
  timeline: {
    labels: string[];
    added: number[];
    paid: number[];
    revenue: number[];
  };
  topBatches: Array<{
    id: string;
    name: string;
    added: number;
  }>;
  dueDateUrgency: {
    overdue?: number;
    this_week?: number;
    this_month?: number;
    later?: number;
  };
  batchBreakdown: {
    added: Array<{ period: string; batchId: string; batchName: string; count: number }>;
    paid: Array<{ period: string; batchId: string; batchName: string; count: number; revenue: number }>;
  };
  recentConversions: any[];
}

// ─────────────────────────────────────────────
// API
// ─────────────────────────────────────────────

export const cartApi = createApi({
  reducerPath: "cartApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/cart",
    credentials: "include",
  }),
  tagTypes: ["Carts"],
  endpoints: (build) => ({

    createCart: build.mutation<Cart, Partial<any>>({
      query: (body) => ({
        url: "/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Carts", id: "LIST" }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (arg?.sku_item_id) {
            dispatch(
              fulfilmentApi.util.invalidateTags([
                "PostalFees",
                "FulfilmentPayments",
                "FulfilmentSummary",
                "FulfilmentPickups",
              ]),
            );
          }
        } catch {
          /* create failed — list stays as-is */
        }
      },
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    getCarts: build.query<{ carts: Cart[]; total: number }, Partial<any>>({
      query: (params) => ({
        url: "/all",
        method: "GET",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.carts.map((cart) => ({ type: "Carts" as const, id: cart.id })),
              { type: "Carts", id: "LIST" },
            ]
          : [{ type: "Carts", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    // ── NEW: flat paginated cart line items ──────────────────────────
    getCartItems: build.query<
      { items: CartItemRow[]; total: number; skip: number; take: number },
      GetCartItemsParams
    >({
      query: (params) => ({
        url: "/items",
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Carts", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    getCartMarketingAnalytics: build.query<CartMarketingAnalyticsResponse, {
      period?: "daily" | "monthly" | "yearly";
      from?: string;
      to?: string;
      batchId?: string;
    }>({
      query: (params) => ({
        url: "/analytics/cart-marketing",
        method: "GET",
        params,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    getCartDetails: build.query<Cart, string>({
      query: (id) => ({
        url: "/details/" + id,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Carts", id }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

    deleteCart: build.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: "/" + id,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Carts", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: any) {
        toastMessage("success", baseQueryReturnValue?.message || "Cart deleted successfully");
        return baseQueryReturnValue;
      },
    }),

    deleteCartItem: build.mutation<{ success: boolean; cart: Cart | null }, string>({
      query: (itemId) => ({
        url: "/item/" + itemId,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Carts", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: any) {
        toastMessage("success", baseQueryReturnValue?.message || "Cart item deleted successfully");
        return baseQueryReturnValue;
      },
    }),

    updateCartItem: build.mutation<{ message: string; cart: Cart }, UpdateCartItemPayload>({
      query: ({ id, ...body }) => ({
        url: "/item/" + id,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "Carts", id: "LIST" }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
      },
    }),

  }),
});

export const {
  useCreateCartMutation,
  useGetCartsQuery,
  useLazyGetCartsQuery,
  useGetCartItemsQuery,
  useLazyGetCartItemsQuery,
  useGetCartMarketingAnalyticsQuery,
  useGetCartDetailsQuery,
  useDeleteCartMutation,
  useDeleteCartItemMutation,
  useUpdateCartItemMutation,
} = cartApi;